// Considered: DropdownAtom/SearchBarAtom/ButtonAtom are reused as-is; no existing molecule renders a
// server-driven member grid with fixed tracks (RelationshipGrid is client-sorted). See spec Reuse Audit.
//
// NOSONAR S6819 S6821 S6823: `role="row"` / `role="cell"` / `role="rowgroup"` on `<div>`s are
// intentional. The roster is a CSS grid (`display: grid`, fixed tracks per the spec's grid contract)
// and NOT an HTML `<table>`: the compact-pane rule collapses the grid to two tracks via `display:none`
// on four columns, which `<td>` inside `<tr>` would break (table-cell reflow does not drop a row's
// column count the same way). The WAI-ARIA grid pattern is explicitly for this case
// (https://www.w3.org/WAI/ARIA/apg/patterns/grid/).
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import type { AggregationMethod, RollupWeighting } from 'types/spherePoints';
import type { FactorRosterMember, FactorRosterResponse, RosterCombineOverrides, RosterExclusion } from '../../../types/statusFactors';
import { fromStored, type RollupWins } from '../RelatedSpheresEditor/combineModel';
import { hasConditions, type RollupSetModel } from '../RollupSetCard/rollupSetModel';
import type { ReadsAsExclusions } from '../RollupSetCard/readsAsSentence';
import { useSpherePermission } from '../../../hooks/useSpherePermission';
import { ROSTER_COLUMNS } from './rosterGrid';
import { ROSTER_PAGE_SIZE, useFactorRoster } from './useFactorRoster';
import MemberCell from './cells/MemberCell';
import ViaCell from './cells/ViaCell';
import ContribCell from './cells/ContribCell';
import StatusCell, { rosterStatusVariant } from './cells/StatusCell';
import WeightCell from './cells/WeightCell';
import ActionsCell, { type RosterActionFailure, type RosterRemoval } from './cells/ActionsCell';
import RollupRosterRemovalStrip from './RollupRosterRemovalStrip';
import RollupRosterExclusions from './RollupRosterExclusions';
import UndoStripMolecule from '../UndoStrip/UndoStripMolecule';
import { RollupRosterHiddenNote } from './RollupRosterCoverage';
import RollupRosterControls from './RollupRosterControls';
import RollupRosterPagination from './RollupRosterPagination';
import './RollupRosterMolecule.scss';

export interface RollupRosterMoleculeProps {
  sphereId: string;
  factorId: string;
  /** The factor's set-card model; an empty expression tree reads as "no conditions" rather than "nothing yet". */
  setModel?: RollupSetModel;
  /** Called when the empty sentence's "Always include" link is chosen; defaults to scrolling the set card's band into view. */
  onAlwaysIncludeClick?: () => void;
  /** Called when a member's contribution is picked: a factor id, or `null` for its overall status. */
  onPickContribution?: (memberSphereId: string, factorId: string | null) => void;
  /** Called when a member whose chosen factor is gone is reset to its overall status. */
  onUseOverall?: (memberSphereId: string) => void;
  /** Display name of the relation the set is read as (the set card's perspective); omitted when unresolved. */
  relationLabel?: string;
  /** The factor's aggregation method; names the method in the explainer and picks its landed status. */
  aggregationMethod?: AggregationMethod;
  /** How each member counts under that method; drives the WEIGHT column and the explainer. */
  weighting?: RollupWeighting;
  /** The factor's own weight toward the sphere, quoted in the explainer. */
  factorWeight?: number;
  /** Called with the exclusions the caller can name each time the roster resolves, so READS AS can quote them. */
  onExclusionsChange?: (excluded: ReadsAsExclusions) => void;
  /** Called each time the roster resolves with its live coverage and how many members have an editable edge weight. */
  onRosterSummaryChange?: (summary: RosterSummary) => void;
  /** True while the host editor is read-only: no WEIGHT input renders. */
  disabled?: boolean;
  /** Unsaved combine settings the rows are computed under (editors only); absent shows the stored roster. */
  combineOverrides?: RosterCombineOverrides;
}

/** What the guardrails read from the roster: coverage over every member the viewer counts, and the edge-backed count. */
export interface RosterSummary {
  coverage: { reporting: number; total: number };
  edgeBackedCount: number;
  /** Why the stored config landed where it did; the guardrails footer renders these as sentences. */
  diagnostics?: string[];
  contributionUnavailableCount?: number;
}

// Measured status labels render as Title Case per the dev handoff.
const STATUS_FALLBACKS: Record<string, string> = {
  on_track: 'On Track',
  at_risk: 'At Risk',
  needs_attention: 'Needs Attention',
  critical: 'Critical',
  blocked: 'Blocked',
  unknown: 'Unknown',
};

const WINS_FALLBACKS: Record<RollupWins, string> = {
  worst: 'Worst wins',
  balanced: 'Balanced',
  majority: 'Majority',
};

const RosterExplainer: React.FC<{
  authoritative: FactorRosterResponse['authoritative'];
  relationLabel?: string;
  aggregationMethod?: AggregationMethod;
  weighting?: RollupWeighting;
  factorWeight?: number;
}> = ({ authoritative, relationLabel, aggregationMethod, weighting, factorWeight }) => {
  const { t } = useTranslation();
  const axes = aggregationMethod ? fromStored({ aggregationMethod, weighting }) : null;
  const landed = axes ? authoritative?.status : undefined;
  const wins = axes ? t(`statusFactors.combine.wins.${axes.wins}.label`, WINS_FALLBACKS[axes.wins]) : undefined;
  const status = landed ? t(`statusFactors.roster.statusValue.${landed}`, STATUS_FALLBACKS[landed] ?? landed) : undefined;
  const relation = relationLabel ?? '';
  const byInfluence = axes ? axes.weighting === 'influence' : true;

  let aggregation: string | null = null;
  if (axes && wins && status) {
    aggregation =
      axes.wins === 'worst'
        ? t('statusFactors.roster.explain.aggregation', '{{method}} lands on {{status}} (read as {{relation}}).', {
            method: wins,
            status,
            relation,
          })
        : t(
            'statusFactors.roster.explain.aggregationCounting',
            '{{method}}, counting members {{counting}}, lands on {{status}} (read as {{relation}}).',
            {
              method: wins,
              counting: byInfluence
                ? t('statusFactors.roster.explain.countingInfluence', 'by influence')
                : t('statusFactors.roster.explain.countingEqual', 'equally'),
              status,
              relation,
            }
          );
  }

  const sentences: Array<string | null> = [
    aggregation,
    byInfluence
      ? t('statusFactors.roster.explain.contribution', "Contribution is each member's share of the blended weight.")
      : t('statusFactors.roster.explain.contributionEqual', 'Every member counts the same.'),
  ];
  if (byInfluence) {
    sentences.push(
      factorWeight === undefined
        ? t('statusFactors.roster.explain.weightMember', "A member's weight here counts toward this rollup.")
        : t(
            'statusFactors.roster.explain.weight',
            "A member's weight here counts toward this rollup; the factor's own weight ({{factorWeight}}) counts toward the sphere.",
            { factorWeight }
          )
    );
  }
  sentences.push(t('statusFactors.roster.explain.roleLinked', 'Role-linked members are read-only.'));

  return (
    <footer className="rollup-roster__explainer" data-testid="roster-explainer">
      {sentences.filter((s): s is string => s !== null).join(' ')}
    </footer>
  );
};

const SKELETON_ROW_COUNT = Math.min(ROSTER_PAGE_SIZE, 5);

function scrollToAlwaysIncludeBand() {
  document.querySelector('[data-testid="always-include-band"]')?.scrollIntoView?.({ block: 'center' });
}

const RosterRow: React.FC<
  Pick<RollupRosterMoleculeProps, 'onPickContribution' | 'onUseOverall'> & {
    member: FactorRosterMember;
    sphereId: string;
    factorId: string;
    onRemoved: (removal: RosterRemoval) => void;
    onFailed: (failure: RosterActionFailure | null) => void;
    failure: RosterActionFailure | null;
    onSetInfluence?: (memberSphereId: string, influence: number) => Promise<void>;
    /** The editor is read-only (deactivated factor or a save in flight): no row action is offered. */
    disabled?: boolean;
  }
> = ({ member, sphereId, factorId, onRemoved, onFailed, failure, onPickContribution, onUseOverall, onSetInfluence, disabled }) => {
  const { t } = useTranslation();
  // Semantic row shading per handoff (P1-8 revised): rows that contribute no measured signal
  // (hidden, not-yet-measured, broken) get the muted surface. Measured rows stay white. This
  // replaces the earlier decorative zebra striping and makes "not reporting" scannable at a glance.
  const variant = rosterStatusVariant(member);
  const rowClass = `rollup-roster__grid rollup-roster__row rollup-roster__row--${variant}`;
  return (
    <>
      <div className={rowClass} role="row" data-testid="roster-row" data-row-variant={variant}>
        <div className="rollup-roster__cell rollup-roster__cell--member" role="cell">
          <MemberCell member={member} onPick={onPickContribution} onUseOverall={onUseOverall} />
        </div>
        <div className="rollup-roster__cell rollup-roster__cell--via" role="cell">
          <ViaCell member={member} />
        </div>
        <div className="rollup-roster__cell rollup-roster__cell--contrib" role="cell">
          <ContribCell member={member} />
        </div>
        <div className="rollup-roster__cell rollup-roster__cell--weight" role="cell">
          <WeightCell member={member} onCommit={onSetInfluence && ((influence) => onSetInfluence(member.sphereId, influence))} />
        </div>
        <div className="rollup-roster__cell rollup-roster__cell--status" role="cell">
          <StatusCell member={member} />
        </div>
        <div className="rollup-roster__cell rollup-roster__cell--actions" role="cell">
          <ActionsCell
            sphereId={sphereId}
            factorId={factorId}
            member={member}
            onRemoved={onRemoved}
            onFailed={onFailed}
            disabled={disabled}
          />
        </div>
      </div>
      {failure && (
        <div role="alert" className="rollup-roster__action-error" data-testid="roster-action-error">
          <span>
            {failure.kind === 'forbidden'
              ? t(
                  'statusFactors.roster.removeForbidden',
                  "Permission denied — {{name}} wasn't removed. Ask someone with edit access to this sphere to change the rollup.",
                  { name: failure.memberName }
                )
              : t(
                  'statusFactors.roster.removeFailedDetail',
                  "Couldn't remove {{name}} — the change didn't save, so it is still in the roster. Try again.",
                  { name: failure.memberName }
                )}
          </span>
          {failure.kind === 'failed' && (
            <ButtonAtom
              variant="text"
              size="small"
              label={t('statusFactors.roster.retry', 'Retry')}
              onClick={failure.retry}
              data-testid="roster-action-retry"
            />
          )}
        </div>
      )}
    </>
  );
};

const SkeletonRow: React.FC = () => (
  <div className="rollup-roster__grid rollup-roster__row" role="row" data-testid="roster-skeleton-row" aria-hidden="true">
    {ROSTER_COLUMNS.map((column) => (
      <div key={column} className={`rollup-roster__cell rollup-roster__cell--${column}`} role="cell">
        <SkeletonAtom height="var(--font-size-sm)" />
      </div>
    ))}
  </div>
);

const RollupRosterMolecule: React.FC<RollupRosterMoleculeProps> = ({
  sphereId,
  factorId,
  setModel,
  onAlwaysIncludeClick,
  onPickContribution,
  onUseOverall,
  relationLabel,
  aggregationMethod,
  weighting,
  factorWeight,
  onExclusionsChange,
  onRosterSummaryChange,
  disabled = false,
  combineOverrides,
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const roster = useFactorRoster(sphereId, factorId, combineOverrides);
  const { allowed: canEdit } = useSpherePermission(sphereId, 'edit');
  const { data, query } = roster;
  const exclusions = data?.exclusions;
  useEffect(() => {
    if (!exclusions) return;
    const names = exclusions.items.flatMap((item) => (!item.hidden && item.sphereName ? [item.sphereName] : []));
    onExclusionsChange?.({ names, total: exclusions.total });
  }, [exclusions, onExclusionsChange]);
  const coverageSummary = data?.projection.coverage;
  const edgeBackedSummary = data?.edgeBackedCount ?? 0;
  const diagnosticsSummary = data?.projection.diagnostics;
  const unavailableSummary = data?.projection.contributionUnavailableCount;
  useEffect(() => {
    if (!coverageSummary) return;
    onRosterSummaryChange?.({
      coverage: { reporting: coverageSummary.reporting, total: coverageSummary.total },
      edgeBackedCount: edgeBackedSummary,
      diagnostics: diagnosticsSummary,
      contributionUnavailableCount: unavailableSummary,
    });
  }, [coverageSummary, edgeBackedSummary, diagnosticsSummary, unavailableSummary, onRosterSummaryChange]);
  const headingRef = useRef<HTMLElement>(null);
  const [actionFailure, setActionFailure] = useState<RosterActionFailure | null>(null);
  // One strip at a time; the id remounts it so a new removal replaces the old one with clean state.
  const [removal, setRemoval] = useState<(RosterRemoval & { id: number }) | null>(null);
  const removalCount = useRef(0);
  // The outcome of a Restore from the exclusions list; replaces any removal strip.
  const [restoreNotice, setRestoreNotice] = useState<{ name: string; failed: boolean; retry?: () => void } | null>(null);

  const handleSetInfluence = async (memberSphereId: string, influence: number) => {
    await roster.setInfluence(memberSphereId, influence);
    logAnalyticsEvent('rollup_member_weight_changed', undefined, { factorId, toZero: influence === 0 });
  };

  const handleRemoved = (next: RosterRemoval) => {
    setRestoreNotice(null);
    roster.refetch();
    if (next.kind === 'excluded') {
      logAnalyticsEvent('rollup_member_excluded', undefined, { factorId, viaKinds: next.viaKinds ?? [] });
    }
    removalCount.current += 1;
    setRemoval({ ...next, id: removalCount.current });
  };

  const undoRemoval = async () => {
    if (!removal) return;
    if (removal.undo) {
      await removal.undo();
      roster.refetch();
      logAnalyticsEvent('rollup_member_unlink_undone', undefined, { factorId });
    } else {
      await roster.restoreExclusion(removal.sphereId);
      logAnalyticsEvent('rollup_exclusion_restored', undefined, { factorId, via: 'undo' });
    }
  };

  const saveExclusionReason: typeof roster.setExclusionReason = async (memberSphereId, reason) => {
    const outcome = await roster.setExclusionReason(memberSphereId, reason);
    if (outcome !== 'gone' && reason.trim() !== '') {
      logAnalyticsEvent('rollup_exclusion_reason_added', undefined, { factorId });
    }
    return outcome;
  };

  const restoreExclusion = async (item: RosterExclusion, name: string) => {
    setRemoval(null);
    try {
      await roster.restoreExclusion(item.sphereId);
      logAnalyticsEvent('rollup_exclusion_restored', undefined, { factorId, via: 'drawer' });
      setRestoreNotice({ name, failed: false });
    } catch (error) {
      setRestoreNotice({ name, failed: true, retry: () => void restoreExclusion(item, name).catch(() => undefined) });
      throw error;
    }
  };

  // The strip unmounts with the focus inside it; the roster heading is the nearest stable target.
  const closeRemoval = () => {
    setRemoval(null);
    headingRef.current?.focus();
  };

  const total = data?.total ?? 0;
  const firstLoad = roster.loading && !data;
  const noConditions = setModel !== undefined && !hasConditions(setModel.tree) && setModel.pins.length === 0;

  // Rows (and skeleton rows) live inside the ARIA table; an empty or error state replaces them outside it.
  let tableBody: React.ReactNode = null;
  let stateArea: React.ReactNode = null;
  let showPagination = false;

  if (roster.error) {
    stateArea = (
      <EmptyStateAtom
        data-testid="roster-error"
        icon="pi-exclamation-triangle"
        title={t('statusFactors.roster.errorTitle', "Couldn't load roster")}
        description={t('statusFactors.roster.errorBody', 'Retry, or check your connection')}
      >
        <ButtonAtom variant="secondary" size="small" label={t('statusFactors.roster.retry', 'Retry')} onClick={roster.refetch} />
      </EmptyStateAtom>
    );
  } else if (firstLoad) {
    tableBody = (
      <div role="rowgroup">
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
          <SkeletonRow key={index} />
        ))}
      </div>
    );
  } else if (data?.members.length === 0) {
    if (roster.filtersActive) {
      stateArea = (
        <EmptyStateAtom
          data-testid="roster-empty-no-match"
          icon="pi-search"
          title={t('statusFactors.roster.empty.noMatch', 'No members match your filters')}
        >
          <ButtonAtom
            variant="secondary"
            size="small"
            label={t('statusFactors.roster.empty.clearFilters', 'Clear filters')}
            onClick={roster.clearFilters}
          />
        </EmptyStateAtom>
      );
    } else if (noConditions) {
      stateArea = (
        <EmptyStateAtom
          data-testid="roster-empty-no-conditions"
          icon="pi-filter"
          title={t('statusFactors.roster.empty.noConditions', 'No conditions — the relation alone defines the set')}
        />
      );
    } else if (data.projection.scopeTotal === 0) {
      stateArea = (
        <EmptyStateAtom
          data-testid="roster-empty-nothing-to-roll-up"
          icon="pi-inbox"
          title={t('statusFactors.roster.empty.nothingToRollUp', 'Nothing to roll up')}
        />
      );
    } else {
      stateArea = (
        <EmptyStateAtom
          data-testid="roster-empty-nothing-yet"
          icon="pi-inbox"
          title={t('statusFactors.roster.empty.nothingYetLead', 'Nothing in this set yet')}
          description={
            <>
              {t(
                'statusFactors.roster.empty.nothingYetBody',
                'it fills itself as spheres start matching, or add one under'
              )}{' '}
              <button type="button" className="rollup-roster__link" onClick={onAlwaysIncludeClick ?? scrollToAlwaysIncludeBand}>
                {t('statusFactors.roster.empty.alwaysIncludeLink', 'Always include')}
              </button>{' '}
              {t('statusFactors.roster.empty.nothingYetTail', 'to seed it')}
            </>
          }
        />
      );
    }
  } else {
    showPagination = true;
    tableBody = (
      <div
        role="rowgroup"
        className={roster.loading ? 'rollup-roster__rows rollup-roster__rows--loading' : 'rollup-roster__rows'}
      >
        {data?.members.map((member) => (
          <RosterRow
            key={member.sphereId}
            member={member}
            sphereId={sphereId}
            factorId={factorId}
            onRemoved={handleRemoved}
            onFailed={setActionFailure}
            failure={actionFailure?.memberSphereId === member.sphereId ? actionFailure : null}
            onPickContribution={onPickContribution}
            onUseOverall={onUseOverall}
            onSetInfluence={canEdit && !disabled ? handleSetInfluence : undefined}
            disabled={disabled}
          />
        ))}
      </div>
    );
  }

  return (
    <section className="rollup-roster" data-testid="rollup-roster" aria-busy={roster.loading}>
      <header ref={headingRef} tabIndex={-1} className="rollup-roster__header-bar" data-testid="roster-header-bar">
        <span className="rollup-roster__eyebrow">
          {relationLabel
            ? t('statusFactors.roster.eyebrow', 'The roster · read as {{relation}}', { relation: relationLabel })
            : t('statusFactors.roster.eyebrowPlain', 'The roster')}
        </span>
        {roster.overridesUnverified && (
          <span role="status" className="rollup-roster__subtitle" data-testid="roster-overrides-unverified">
            {t(
              'statusFactors.roster.overridesUnverified',
              "Couldn't check these rows against your unsaved combine settings — showing the saved roster."
            )}
          </span>
        )}
        <span className="rollup-roster__subtitle">
          {t('statusFactors.roster.subtitle', 'derived from the set above — add a sphere under')}{' '}
          <button type="button" className="rollup-roster__link" onClick={onAlwaysIncludeClick ?? scrollToAlwaysIncludeBand}>
            {t('statusFactors.roster.alwaysIncludeLink', 'Always include')}
          </button>
        </span>
      </header>

      <RollupRosterControls roster={roster} disabled={firstLoad} />

      {/* P1-4: The "N of N reporting" range line was removed to match the handoff; the same number
          lives at the bottom in "Showing 1–N of N" on the pager. RollupRosterCoverage stays exported
          for any future needs. */}

      {removal && (
        <RollupRosterRemovalStrip
          key={removal.id}
          removal={removal}
          undo={undoRemoval}
          setExclusionReason={saveExclusionReason}
          onClose={closeRemoval}
        />
      )}

      {restoreNotice && (
        <UndoStripMolecule
          className="rollup-roster__strip"
          message={
            restoreNotice.failed
              ? t(
                  'statusFactors.roster.exclusions.restoreFailed',
                  "Couldn't restore {{name}} — the change didn't save, so it is still excluded. Try again.",
                  { name: restoreNotice.name }
                )
              : t(
                  'statusFactors.roster.exclusions.restored',
                  "{{name}} restored — it's back if the set still matches it",
                  { name: restoreNotice.name }
                )
          }
          variant={restoreNotice.failed ? 'error' : 'default'}
          undoLabel={t('statusFactors.roster.undo', 'Undo')}
          dismissLabel={t('statusFactors.roster.undoStrip.dismiss', 'Dismiss')}
          retryLabel={t('statusFactors.roster.retry', 'Retry')}
          onRetry={restoreNotice.failed ? restoreNotice.retry : undefined}
          onDismiss={() => setRestoreNotice(null)}
        />
      )}

      <div role="table">
        <div className="rollup-roster__grid rollup-roster__header" role="row" data-testid="roster-header">
          {ROSTER_COLUMNS.map((column) => (
            <div key={column} className={`rollup-roster__cell rollup-roster__cell--${column}`} role="columnheader">
              {t(`statusFactors.roster.${column}`, column)}
            </div>
          ))}
        </div>
        {tableBody}
      </div>
      {stateArea}

      {showPagination && data && data.hiddenCount > 0 && <RollupRosterHiddenNote hiddenCount={data.hiddenCount} />}

      {showPagination && (
        <RollupRosterPagination
          page={Math.floor(query.offset / ROSTER_PAGE_SIZE)}
          pageSize={ROSTER_PAGE_SIZE}
          total={total}
          onPageChange={(page) => roster.setOffset(page * ROSTER_PAGE_SIZE)}
        />
      )}

      {data && (
        <RollupRosterExclusions sphereId={sphereId} exclusions={data.exclusions} onRestore={restoreExclusion} />
      )}

      {showPagination && data && (
        <RosterExplainer
          authoritative={data.authoritative}
          relationLabel={relationLabel}
          aggregationMethod={aggregationMethod}
          weighting={weighting}
          factorWeight={factorWeight}
        />
      )}
    </section>
  );
};

export default RollupRosterMolecule;
